import { computed, PropType, Ref, ref, unref, watch } from 'vue';

import { isEqual } from '@/helpers/utils/objects';
import { DataType, getPropDefault, getPropType } from '@/helpers/utils/props';

export interface UseComputedLabelAndId {
  computedLabel: Ref<string>;
  computedId: Ref<string>;
}

/** Use generated label and id to be added to the input component */
export function useComputedLabelAndId(
  { t },
  props: {
    rules: string | Record<string, string>;
    label: string;
    name: string;
    hideOptionalText?: boolean;
  },
): UseComputedLabelAndId {
  const isOptional = computed(
    () =>
      (typeof props.rules === 'string' && !props.rules.includes('required')) ||
      (typeof props.rules !== 'string' && !props.rules.required),
  );
  const optionalText = computed(() => `(${t('form.optionalField')})`);
  const computedLabel = computed(() =>
    props.label
      ? `${props.label} ${isOptional.value && !props.hideOptionalText ? optionalText.value : ''}`
      : '',
  );

  const computedId = computed(() => `${props.name}-input`);

  return {
    computedLabel,
    computedId,
  };
}

/** Returns prop types of `UseAsUpdateChildInput` component */
export function getUpdateChildInputProps<T>(
  type: DataType,
  defaultValue: T,
): { modelValue: { type: PropType<T>; default: T | (() => T) } } {
  /** The current value of the input */
  return {
    modelValue: {
      type: getPropType(type) as PropType<T>,
      default: getPropDefault(type, defaultValue),
    },
  };
}

/** Use current component as child component with a single input. Works in combination with v-model
 * @param defaultValue value to be used as default value for the child input
 * @param conversionFunction function that should be called before emitting the update event to convert the input value
 * @param unwantedValue value that should not update the current input value
 * @param oneWay indicator, if the component should just call the conversion function when emitting
 * @param skipEmit indicator, if the emit of the updated value should be skipped
 */
export function useAsUpdatingChildInput<
  TValue,
  TContext extends {
    emit: (message: 'update:modelValue', ...payload: any[]) => void;
  },
>(
  context: TContext,
  parentValue: Ref<TValue | null>,
  defaultValue: TValue,
  {
    conversionFunction,
    oneWayConversion,
    unwantedValue,
    skipEmit,
  }: Partial<{
    conversionFunction: (x: TValue | null) => unknown;
    unwantedValue: TValue | null;
    oneWayConversion: boolean;
    skipEmit: Ref<boolean> | boolean;
  }> = {},
): Ref<TValue> {
  const childValue = ref<TValue>(defaultValue) as Ref<TValue>;

  watch(
    parentValue,
    (value) => {
      const newValue = (
        conversionFunction && !oneWayConversion ? conversionFunction(value) : value
      ) as TValue;

      /* do not update if already equal to child  */
      if (isEqual(newValue, childValue.value)) return;

      childValue.value = newValue;
    },
    { immediate: true },
  );

  watch(
    childValue,
    (newValue) => {
      if (unref(skipEmit)) return;

      /** do not emit new value, if unwanted value present */
      if (unwantedValue !== undefined && newValue === unwantedValue) return;

      /* do not update if already equal to parent */
      if (isEqual(newValue, parentValue.value)) return;

      context.emit(
        'update:modelValue',
        conversionFunction ? conversionFunction(newValue) : newValue,
      );
    },
    { deep: true },
  );

  return childValue;
}

/** Returns the width of the displayed text depending on the used font */
export function getDisplayedTextWidth(
  text: string,
  font = '400 16px Montserrat',
  letterSpacing = '0',
): number {
  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d');
  if (!canvasContext) return 0;
  canvasContext.font = font;
  canvasContext.letterSpacing = letterSpacing;
  const metrics = canvasContext.measureText(text);
  return metrics.width;
}
